import flatten from 'lodash.flatten';

import { ALBUM_FOLDER_TAG_TYPE } from '../../../../../constants/album-folder-tags';
import { ALBUM_SUBTYPE } from '../../../../../constants/albums';
import { HIERARCHY_SHOOT_TYPE } from '../../../../../constants/shoot-types';
import { getTagsByType } from '../../../../../util/album-folder-tags';

/**
 * Checks whether the AlbumFolder should solicit tags during onboarding.
 *
 * @param {Object} albumFolder The AlbumFolder.
 * @returns {boolean}
 */
export const isAlbumFolderTagSelectionRequired = albumFolder => {
  return !!albumFolder?.requires_tag_onboarding;
};

export const hasAlbumWithTagJoinsDisabled = albumFolder => {
  if (albumFolder?.children?.albums.records?.length > 0) {
    return !!albumFolder.children.albums.records.find(
      album => album.tag_joins_disabled,
    );
  }

  if (albumFolder?.children?.album_folders?.records?.length > 0) {
    return !!albumFolder.children.album_folders.records.find(subFolder => {
      if (subFolder?.children?.albums?.records?.length > 0) {
        return !!subFolder.children.albums.records.find(
          album => album.tag_joins_disabled,
        );
      }

      return false;
    });
  }

  return false;
};

/**
 * Checks whether the AlbumFolder actually performs numbered jersey recognition,
 * and therefore whether a jersey number is worth soliciting during onboarding.
 *
 * Jersey numbers only ever match action photography, so a portrait only event
 * has no use for them -- and neither does a portrait & action event that has
 * not got a single action Album to match against.
 *
 * @param {Object} options The options.
 * @param {HIERARCHY_SHOOT_TYPE} options.shootType The AlbumFolder's shoot type.
 * @param {Number} options.albumsTotal How many Albums the event holds.
 * @param {Number} options.actionAlbumsTotal How many of those are action Albums.
 * @returns {boolean}
 */
export const albumFolderRequiresNumberedJerseyRecognition = ({
  shootType,
  albumsTotal = 0,
  actionAlbumsTotal = 0,
} = {}) => {
  if (shootType === HIERARCHY_SHOOT_TYPE.PORTRAIT) {
    return false;
  }

  // An event with no Albums at all tells us nothing -- it has simply not been
  // photographed yet -- so keep the recognition on rather than withholding the
  // very number the matching depends on.
  if (
    shootType === HIERARCHY_SHOOT_TYPE.PORTRAIT_AND_ACTION &&
    albumsTotal > 0
  ) {
    return actionAlbumsTotal > 0;
  }

  return true;
};

/**
 * Checks whether the user MUST complete tag information
 * in order to onboard an AlbumFolder.
 *
 * @param {Object} albumFolder The AlbumFolder.
 * @returns {null|*}
 */
export const albumFolderRequiresTagsToOnboard = albumFolder => {
  if (hasAlbumWithTagJoinsDisabled(albumFolder)) {
    return false;
  }

  return albumFolder?.tag_join_enabled;
};

/**
 * Checks the input of IdentityTagSelection and determines whether the user
 * can continue with onboarding (whether they have filled enough info).
 *
 * @param {Object} options The options.
 * @param {Object} options.albumFolder The AlbumFolder.
 * @param {Array} options.albumFolderTags The AlbumFolderTag that are being configured
 * @param {Object} options.input The AlbumFolderTagCreateInput.
 * @param {Boolean} [options.numberedJerseyRecognitionEnabled=true] Whether the
 *  AlbumFolder performs numbered jersey recognition. When it does not, no
 *  jersey number was ever asked for, so none can be required.
 * @returns {boolean}
 */
export const hasAvailableUniformTagInfoForOnboarding = ({
  albumFolder,
  albumFolderTags,
  input,
  numberedJerseyRecognitionEnabled = true,
}) => {
  // If the AlbumFolder does not require tags complete info
  // to be onboarded return true and let the user continue.
  if (!albumFolderRequiresTagsToOnboard(albumFolder)) {
    return true;
  }

  // If the AlbumFolderTag is not available or has no levels (probably by mistake)
  // simply return true and let the user continue.
  if (
    albumFolderTags.length === 0 ||
    !!albumFolderTags.find(({ levels }) => levels?.records?.length === 0)
  ) {
    return true;
  }

  // Otherwise the user must fill their level, team, and jersey on each selected tag to continue
  // onboarding.
  let tagDataFilled = true;
  albumFolderTags.forEach(albumFolderTag => {
    if (!tagDataFilled) {
      return;
    }

    const albumFolderTagInput = input?.album_folder_tags?.find(
      tag => tag.album_folder_tag_id === albumFolderTag.id,
    );

    const requiresLevel = albumFolderTag?.levels?.records?.length > 0;
    const requiresJersey =
      numberedJerseyRecognitionEnabled &&
      albumFolderTag?.uniform_enabled &&
      albumFolderTag?.uniform_numbered;

    // Level + Team Check
    if (requiresLevel) {
      const inputLevelId =
        albumFolderTagInput?.uniform?.uniform_album_folder_tag_level_id;

      if (!inputLevelId) {
        tagDataFilled = false;
      }

      const level = albumFolderTag.levels.records.find(
        ({ id: levelId }) => levelId === inputLevelId,
      );
      const requiresTeam = level?.teams?.records?.length > 0;

      if (requiresTeam) {
        const inputTeamId =
          albumFolderTagInput?.uniform?.uniform_album_folder_tag_level_team_id;

        if (!inputTeamId) {
          tagDataFilled = false;
        }
      }
    }

    // Jersey Check
    if (requiresJersey) {
      const inputJersey = albumFolderTagInput?.uniform?.jersey_number;

      if (!inputJersey) {
        tagDataFilled = false;
      }
    }
  });

  return tagDataFilled;
};

/**
 * Checks if the selected tag by a user is at least partially
 * filled in.
 *
 * @param {Object} selectedTag The selected tag configuration object as produced by the tag selection form.
 * @param {Array} configuringTagIds The user selected tags to configure
 * @returns {boolean}
 */
export const hasConfiguredSelectedTag = (
  selectedTag,
  configuringTagIds = [],
) => {
  if (!selectedTag) {
    return false;
  }

  const { tagId, levelId, teamId, jerseyNumber } = selectedTag;

  return (
    configuringTagIds.includes(tagId) || !!levelId || !!teamId || !!jerseyNumber
  );
};

/**
 * Checks whether the user can configure an additional team for
 * a particular tag during tags onboarding by checking whether
 * all tags are complete.
 *
 * @param {Array} selectedTags The selected tags configuration array as produced by the tag selection form.
 * @returns {*}
 */
export const canConfigureAdditionalTeam = (selectedTags = []) => {
  // TODO [tags] Disable multiple team selection until platform is ready

  if (true) {
    return false;
  }

  return selectedTags.every(hasConfiguredSelectedTag);
};

/**
 * Given an AlbumFolder's `AlbumFolderTags` and an Identity's existing
 * `IdentityAlbumFolderTags` it produces the initial state for the
 * tag selection form.
 *
 * @param {Array} albumFolderTags The AlbumFolderTags.
 * @param {Array} identityAlbumFolderTags The IdentityAlbumFolderTags.
 * @returns {Object} The initial state of the form.
 */
export const mapIdentityAlbumFolderTagsToSelectedTags = (
  albumFolderTags = [],
  identityAlbumFolderTags = [],
) => {
  // First map over existing identityAlbumFolderTags
  const existingState = identityAlbumFolderTags.reduce(
    (acc, identityAlbumFolderTag) => {
      const albumFolderTagId = identityAlbumFolderTag.album_folder_tag.id;

      return {
        ...acc,
        [albumFolderTagId]: [
          ...(acc[albumFolderTagId] || []),
          {
            tagId: albumFolderTagId,
            levelId:
              identityAlbumFolderTag.uniform_album_folder_tag_level?.id || '',
            teamId:
              identityAlbumFolderTag.uniform_album_folder_tag_level_team?.id ||
              '',
            jerseyNumber: identityAlbumFolderTag.jersey_number || '',
            isNumberedJerseys:
              !!identityAlbumFolderTag.album_folder_tag?.uniform_numbered,
          },
        ],
      };
    },
    {},
  );

  // Fill in the rest of the tags (empty)
  return albumFolderTags.reduce((acc, albumFolderTag) => {
    if (acc[albumFolderTag.id]) {
      return acc;
    }

    return {
      ...acc,
      [albumFolderTag.id]: [
        {
          tagId: albumFolderTag.id,
          levelId: '',
          teamId: '',
          jerseyNumber: '',
          isNumberedJerseys: !!albumFolderTag.uniform_numbered,
        },
      ],
    };
  }, existingState);
};

/**
 * Maps selectegTags coming from the IdentityTagSelection form into
 * `IdentityAlbumFolderTags` create/update input.
 *
 * @param {Object} identity The identity.
 * @param {Object} selectedTags The selected tags configuration object as produced by the tag selection form.
 * @param {Array} configuringTagIds The user selected tags to configure
 * @returns {{identity_id: *, album_folder_tags: {uniform: {uniform_album_folder_tag_level_team_id: (*|undefined), uniform_album_folder_tag_level_id: (*|undefined), jersey_number: (*|undefined)}, album_folder_tag_id: *}[]}|null}
 */
export const mapSelectedTagsToIdentityAlbumFolderTagsInput = (
  identity,
  selectedTags,
  configuringTagIds = [],
) => {
  if (!identity) {
    return null;
  }

  const tags = flatten(Object.values(selectedTags));

  const validTags = tags.filter(selTag =>
    hasConfiguredSelectedTag(selTag, configuringTagIds),
  );

  if (!validTags.length) {
    return null;
  }

  return {
    identity_id: identity.id,
    album_folder_tags: validTags.map(tag => ({
      album_folder_tag_id: tag.tagId,
      uniform: {
        uniform_album_folder_tag_level_id: tag.levelId || undefined,
        uniform_album_folder_tag_level_team_id: tag.teamId || undefined,
        jersey_number: (tag.jerseyNumber ?? '').trim() || undefined,
      },
    })),
  };
};

/**
 * Filters out AlbumFolderTags that are not eligible by an identity to "join"
 * based on cross referencing their gender.
 *
 * @param {Array<Object>} albumFolderTags The AlbumFolderTags.
 * @param {IDENTITY_GENDER} identityGender The identity's gender.
 * @param {Boolean} uniformEnabledOnly whether or not to include all tags, or only those with uniforms enabled
 * @returns {Array<Object>}
 */
export const getEligibleTagsByIdentityGender = (
  albumFolderTags,
  identityGender,
  uniformEnabledOnly = false,
) => {
  if (!identityGender) {
    return uniformEnabledOnly
      ? albumFolderTags.filter(albumFolderTag => albumFolderTag.uniform_enabled)
      : albumFolderTags;
  }

  // Since it is possible uniformEnabledOnly is available even while having no uniform_enabled
  // tags, we will add a safeguard to prevent not showing anything unintentionally.
  if (
    albumFolderTags.some(albumFolderTag => albumFolderTag.uniform_enabled) &&
    uniformEnabledOnly
  ) {
    return albumFolderTags.filter(
      albumFolderTag =>
        (albumFolderTag.gender === identityGender || !albumFolderTag.gender) &&
        albumFolderTag.uniform_enabled,
    );
  }

  return albumFolderTags.filter(
    albumFolderTag =>
      albumFolderTag.gender === identityGender || !albumFolderTag.gender,
  );
};

/**
 * Checks if a UniformAlbumFolderTag is configurable.
 *
 * @param {Object} uniformAlbumFolderTag The uniform AlbumFolderTag.
 * @returns {boolean}
 */
export const isUniformAlbumFolderTagConfigurable = uniformAlbumFolderTag => {
  if (!uniformAlbumFolderTag) {
    return false;
  }

  return (
    uniformAlbumFolderTag.levels?.records.length > 0 ||
    uniformAlbumFolderTag.uniform_numbered
  );
};

/**
 * Checks whether the AlbumFolder should solicit grade tags during onboarding.
 *
 * @param {Object} albumFolder The AlbumFolder.
 * @returns {boolean}
 */
export const isAlbumFolderGradeTagSelectionRequired = albumFolder => {
  return (
    albumFolder?.album_subtype === ALBUM_SUBTYPE.SCHOOLS &&
    getTagsByType(ALBUM_FOLDER_TAG_TYPE.GRADE, albumFolder?.tags?.records ?? [])
      .length > 0
  );
};

/**
 * Determines if the levels of a given tag contain teams or not.
 *
 * @param {Object} albumFolderTag the AlbumFolderTag
 * @returns {boolean}
 */
export const albumFolderTagHasTeams = albumFolderTag => {
  if (!albumFolderTag || (albumFolderTag.levels?.records?.length ?? 0) === 0) {
    return false;
  }
  return !!albumFolderTag.levels.records.find(
    level => level.teams.records.length > 0,
  );
};

/**
 * Retrieves the invalid jersey number from the selected tags configuration passed
 *
 * @param {Array} selectedTags the selected tags
 *
 * @returns {Array}
 */
export const getInvalidJerseyNumbersFromSelectedTags = selectedTags => {
  const invalidJerseyNumbers = [];
  const jerseyNumberRegexp = /^[0-9]{1,2}$/;

  if (!selectedTags) {
    return invalidJerseyNumbers;
  }

  const selectedTagIds = Object.keys(selectedTags);

  selectedTagIds.forEach(tagId => {
    const configuredTags = selectedTags[tagId];

    configuredTags.forEach(({ levelId, jerseyNumber, isNumberedJerseys }) => {
      // Ignore any tags w/o level id. These cannot be submitted and may be
      // generated when certain gendered sports unavailable to the identity exist.
      // Also, ignore those tags that do not require numbered jerseys.
      if (!levelId || !isNumberedJerseys) {
        return;
      }

      const trimmedJerseyNumber = (jerseyNumber ?? '').trim();

      if (!trimmedJerseyNumber) {
        return;
      }

      if (!jerseyNumberRegexp.test(trimmedJerseyNumber)) {
        invalidJerseyNumbers.push(jerseyNumber);
      }
    });
  });

  return invalidJerseyNumbers;
};
